import { useTranslations } from 'next-intl';
import { type FC, type ReactNode, useCallback, useMemo, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';

import clsx from 'clsx';
import isEqual from 'lodash/isEqual';

import { Chart } from '@/app/[locale]/(authorized)/(dashboard)/components/Dashboard/components/Charts/components/Chart';
import { EmptyPieComparisonPlaceholder } from '@/app/[locale]/(authorized)/(dashboard)/components/Dashboard/components/Charts/components/common/EmptyPieComparisonPlaceholder/EmptyPieComparisonPlaceholder';
import { SalesCountComparisonChartFilter } from '@/app/[locale]/(authorized)/(dashboard)/components/Dashboard/components/Charts/components/SalesCountComparisonChart/components/SalesCountComparisonChartFilter';
import { useSalesPerCurrencyChartData } from '@/app/[locale]/(authorized)/(dashboard)/components/Dashboard/components/Charts/hooks/use-sales-per-currency-chart-data';
import { getComparisonGateNames } from '@/app/[locale]/(authorized)/(dashboard)/components/Dashboard/components/Charts/utils/get-comparison-gate-names';
import type { GateGroupDetails } from '@/app/[locale]/(authorized)/payment-gates/[id]/components/PaymentGate/hooks/use-gate-group-details';
import { PieChart, type Sector } from '@/components/common/charts/PieChart';
import { Flex } from '@/components/common/Flex';
import { Spinner } from '@/components/common/Spinner';
import { type WithClassName } from '@/types/react';
import { type RegularColor } from '@/types/theme';

import styles from './SalesCountComparisonChart.module.scss';

type SalesCountComparisonChartProps = WithClassName & {
  title: ReactNode;
  gateGroupDetails?: GateGroupDetails;
};

export const SalesCountComparisonChart: FC<SalesCountComparisonChartProps> = (props) => {
  const t = useTranslations('pages.dashboard.widgets.comparisonPlaceholder');
  const { className, title, gateGroupDetails } = props;
  const [filter, setFilter] = useState<SalesCountComparisonChartFilter | undefined>();

  const {
    data,
    loading,
    refetch: refetchData,
  } = useSalesPerCurrencyChartData({
    filter,
  });

  const sectors = useMemo((): Sector[] => {
    if (!data?.dashboardWidgetsSalesPerCurrencyChartV2) {
      return [];
    }

    const getSector = (index: 1 | 2, color: RegularColor, legendLabel: string): Sector => {
      const gateGroupData = data.dashboardWidgetsSalesPerCurrencyChartV2[`gate_group_${index}`];

      return {
        id: gateGroupData.gate_group_id,
        name: gateGroupData.gate_group_name,
        value: gateGroupData.value,
        color,
        legendLabel,
      };
    };

    return [getSector(1, 'green', t('gate1')), getSector(2, 'yellow', t('gate2'))];
  }, [data?.dashboardWidgetsSalesPerCurrencyChartV2, t]);

  const gateNames = useMemo(
    () => getComparisonGateNames(data?.dashboardWidgetsSalesPerCurrencyChartV2, filter),
    [data?.dashboardWidgetsSalesPerCurrencyChartV2, filter]
  );

  const hasTransactions = useMemo(() => {
    return sectors.some(({ value }) => value > 0);
  }, [sectors]);

  const onFilter = useCallback<SubmitHandler<SalesCountComparisonChartFilter>>(
    (data) => {
      setFilter(data);

      // Если ничего не поменялось, аполло сам не перезапустит query
      if (isEqual(data, filter)) {
        refetchData();
      }
    },
    [filter, refetchData]
  );

  return (
    <Chart
      className={clsx(styles.root, className)}
      chartClassName={styles.chart}
      title={title}
      filter={
        <SalesCountComparisonChartFilter
          defaultValues={filter}
          gateGroupDetails={gateGroupDetails}
          onSubmit={onFilter}
        />
      }
      chart={
        loading ? (
          <Flex alignItems="center" justifyContent="center">
            <Spinner size={56} />
          </Flex>
        ) : hasTransactions ? (
          <PieChart className={styles.pie} sectors={sectors} withLegend />
        ) : (
          <EmptyPieComparisonPlaceholder className={styles.empty} gateNames={gateNames} />
        )
      }
    />
  );
};
